/* eslint-disable max-classes-per-file */
import { Component } from "react"
import PropTypes from "prop-types"

import { createOrderByFilterGroup, genericFilterGroup } from "shared/search/containers/FilterGroup/FilterGroups"
import { createCustomFilterGroup } from "shared/search/containers/FilterGroup/CustomFilterGroup"

import {
    createListFilter,
    createNotInListFilter,
    ManuallyChangedInListFilter,
    // common filters
    RegionFilter,
    RegionCountryFilter,
    RegionCountryEUFilter,
    RepresentingCountyFilter,
    RepresentingMunicipalityFilter,
    RepresentingZipCodeFilter,
} from "shared/search/containers/Filter/Filters"
import { inIssueFilter, notInIssueFilter } from "shared/search/containers/Filter/FilterCreators"

import { PersonRepresentingCountryEUFilter } from "shared/search/containers/Filter/OfficialFilters"

import {
    EUPublicOrganizationCustomTypeFilter,
    PublicOrganizationTypeFilter,
    EUNewMajorOrganizationTypeFilter,
    PublicOrganizationOutletMetaTypeFilter,
    PublicOrganizationTypeInternationalFilter,
    PublicOrganizationParentSearchifyFilter,
    PublicOrganizationFromBulkUploadFilter,
    PublicOrganizationProjectFilter,
    // organization profile
    PublicOrganizationOrganizatonsFilter,
    PublicOrganizationTransactionFilter,
    PublicOrganizationPacOrgTypeFilter,
    // Outlets
    PublicOrganizationArticleFilter,
    PublicOrganizationDMAFilter,
    PublicOrganizationSchoolDistrictFilter,
    PublicOrganizationCirculationFilter,
    PublicOrganizationFrequencyFilter,
    PublicOrganizationLanguageFilter,
    PublicOrganizationOutletCoverageFilter,
    PublicOrganizationOutletTypeFilter,
    PublicOrganizationPressFilter,
    PublicOrganizationSocialMediaFilter,
    PublicOrganizationDocumentFilter,
    OrganizationSchoolDistrictEnrollmentMaxFilter,
    OrganizationSchoolDistrictEnrollmentMinFilter,
} from "shared/search/containers/Filter/PublicOrganizationFilters"

// helpers
import { isFeatureEnabled } from "shared/featureflags/helperFunctions"
import { checkIfShouldShowSchoolBoard } from "shared/imports/permissionFunctions"
import { bulkUploadIsAvailable } from "shared/search/helperFunctions"

const defaultProps = {
    filters: PropTypes.array,
    uniqueFilterGroupId: PropTypes.string,
    displayLabel: PropTypes.string,
    helpText: PropTypes.string,
}
const { isExclusivelySchoolBoard, canSeeSchoolBoards } = checkIfShouldShowSchoolBoard()

export class PublicOrganizationTypeFilterGroup extends Component {
    // shouldShowFunc is called here because there is the case that
    // PublicOrganizationTypeFilter has no enum items to render, and so it
    // won't render at all, leaving an empty filter group which just looks
    // tacky. Might be useful to generalize in this future.
    static defaultProps = {
        filters: [
            PublicOrganizationTypeFilter,
            // For School Boards, we need to check if the user doesn't have other local permissions.
            // This is due the overlapping between both permissions and what we actually need to show in the filter.
            ...(isExclusivelySchoolBoard
                ? []
                : [
                      EUPublicOrganizationCustomTypeFilter,
                      EUNewMajorOrganizationTypeFilter,
                      PublicOrganizationTypeInternationalFilter,
                      PublicOrganizationOutletMetaTypeFilter,
                  ]),
        ],
        uniqueFilterGroupId: "publicOrgTypeFilterGroup",
        displayLabel: "Organization Type",
        helpText: "Filter organizations by type.",
        permissions: [DjangIO.app.models.PermissionType.legislators, DjangIO.app.models.PermissionType.media_relations],
        shouldShowFunc: () =>
            Boolean(PublicOrganizationTypeFilter.defaultProps.enumItems.length) ||
            Boolean(EUNewMajorOrganizationTypeFilter.defaultProps.enumItems.length) ||
            Boolean(PublicOrganizationTypeInternationalFilter.defaultProps.enumItems.length) ||
            Boolean(PublicOrganizationOutletMetaTypeFilter.defaultProps.enumItems.length),
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class PublicOrganizationParentFilterGroup extends Component {
    static defaultProps = {
        filters: [PublicOrganizationParentSearchifyFilter],
        uniqueFilterGroupId: "publicOrgParentFilterGroup",
        displayLabel: "Parent Organization",
        helpText: "Filter organizations by parent organization.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export const PublicOrganizationCustomFilterGroup = createCustomFilterGroup(
    "organizationCustomFilterGroup",
    DjangIO.app.models.QuorumDataType.public_organization.value,
)

export class PublicOrganizationRegionFilterGroup extends Component {
    static defaultProps = {
        filters: [
            RegionFilter,
            RegionCountryFilter,
            RegionCountryEUFilter,
            RepresentingCountyFilter,
            RepresentingMunicipalityFilter,
            ...(canSeeSchoolBoards
                ? [
                      PublicOrganizationSchoolDistrictFilter,
                      ...(isFeatureEnabled("ff_school_board_orgs_and_officials")
                          ? [
                                OrganizationSchoolDistrictEnrollmentMinFilter,
                                OrganizationSchoolDistrictEnrollmentMaxFilter,
                            ]
                          : []),
                  ]
                : []),
            RepresentingZipCodeFilter,
            PublicOrganizationDMAFilter,
        ],
        uniqueFilterGroupId: "publicOrgRegionFilterGroup",
        displayLabel: "Region",
        helpText: "Filter for organizations from a single region or group of regions.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class PublicOrganizationListFilterGroup extends Component {
    static defaultProps = {
        filters: [
            createListFilter(DjangIO.app.models.QuorumDataType.public_organization.value),
            createNotInListFilter(DjangIO.app.models.QuorumDataType.public_organization.value),
            ManuallyChangedInListFilter,
        ],
        uniqueFilterGroupId: "publicOrgListFilterGroup",
        displayLabel: "Lists",
        helpText: "Filter for organizations from a single region or group of regions.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export class PublicOrganizationDataMaintenanceFilterGroup extends Component {
    static defaultProps = {
        filters: bulkUploadIsAvailable(DjangIO.app.custom_data.types.BulkUploadType.public_organization)
            ? [PublicOrganizationFromBulkUploadFilter]
            : [],
        uniqueFilterGroupId: "orgDataMaintenanceFilterGroup",
        displayLabel: "Data Maintenance",
        helpText: "Filter organizations by source.",
    }

    render() {
        return genericFilterGroup(this.props)
    }
}

export const PublicOrganizationOrganizationProfileFilterGroup = (props) => genericFilterGroup(props)
PublicOrganizationOrganizationProfileFilterGroup.propTypes = defaultProps
PublicOrganizationOrganizationProfileFilterGroup.defaultProps = {
    filters: [PublicOrganizationOrganizatonsFilter],
    uniqueFilterGroupId: "publicOrganizationOrganizationProfileFilterGroup",
    noHeader: true,
    alwaysOpen: true,
}

export const PublicOrganizationProjectFilterGroup = (props) => genericFilterGroup(props)
PublicOrganizationProjectFilterGroup.propTypes = defaultProps
PublicOrganizationProjectFilterGroup.defaultProps = {
    filters: [inIssueFilter, notInIssueFilter],
    uniqueFilterGroupId: "publicOrganizationProjectFilterGroup",
    displayLabel: "Issues",
    helpText: "Filter results by the issue they are tagged to.",
}

export const PublicOrganizationPacFilterGroup = (props) => genericFilterGroup(props)
PublicOrganizationPacFilterGroup.propTypes = defaultProps
PublicOrganizationPacFilterGroup.defaultProps = {
    filters: [PublicOrganizationPacOrgTypeFilter, PublicOrganizationTransactionFilter],
    uniqueFilterGroupId: "publicOrganizationPacFilterGroup",
    displayLabel: "PAC",
    helpText: "Filter organizations related to PAC.",
}

// Outlet Specific FilterGroups
export const PublicOrganizationOutletFilterGroup = (props) => genericFilterGroup(props)
PublicOrganizationOutletFilterGroup.propTypes = defaultProps
PublicOrganizationOutletFilterGroup.defaultProps = {
    filters: [
        PublicOrganizationOutletTypeFilter,
        PublicOrganizationOutletCoverageFilter,
        PublicOrganizationFrequencyFilter,
        PublicOrganizationCirculationFilter,
        PublicOrganizationLanguageFilter,
        PublicOrganizationArticleFilter,
        PublicOrganizationPressFilter,
    ],
    uniqueFilterGroupId: "publicOrganizationOutletFilterGroup",
    displayLabel: "Communications",
    helpText: "Filter for media outlets.",
    permissions: [DjangIO.app.models.PermissionType.media_relations],
}

export const PublicOrganizationOrderByFilterGroup = createOrderByFilterGroup({
    // uniqueFilterId = "public_organizationOrderBy"
    quorumDataType: DjangIO.app.models.QuorumDataType.public_organization.value,
})

export const PublicOrganizationDocumentFilterGroup = (props) => genericFilterGroup(props)
PublicOrganizationDocumentFilterGroup.propTypes = defaultProps
PublicOrganizationDocumentFilterGroup.defaultProps = {
    filters: [PublicOrganizationDocumentFilter],
    uniqueFilterGroupId: "publicOrganizationSocialMediaFilterGroup",
    displayLabel: "Documents",
    helpText: "Filter Organizations by Documents",
    permissions: [DjangIO.app.models.PermissionType.media_relations],
}

export const PublicOrganizationSocialMediaFilterGroup = (props) => genericFilterGroup(props)
PublicOrganizationSocialMediaFilterGroup.propTypes = defaultProps
PublicOrganizationSocialMediaFilterGroup.defaultProps = {
    filters: [PublicOrganizationSocialMediaFilter],
    uniqueFilterGroupId: "publicOrganizationSocialMediaFilterGroup",
    displayLabel: "Social Media",
    helpText: "Filter Organizations by Social Media Documents",
    permissions: [DjangIO.app.models.PermissionType.media_relations],
}
